import React from 'react';
import { Filter, Search, X, CreditCard, Wallet, Gift, Plane, Building2, Tag } from 'lucide-react';

interface FilterProps {
  filters: {
    annualFee: string[];
    cardType: string[];
    bank: string[];
    features: string[];
    cardCategory: string[];
  };
  selectedFilters: {
    annualFee: string[];
    cardType: string[];
    bank: string[];
    features: string[];
    cardCategory: string[];
  };
  onFilterChange: (category: string, value: string) => void;
  onClearFilters: () => void;
  searchTerm: string;
  onSearchChange: (value: string) => void;
}

const CardFilters: React.FC<FilterProps> = ({
  filters,
  selectedFilters,
  onFilterChange,
  onClearFilters,
  searchTerm,
  onSearchChange
}) => {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-lg overflow-hidden">
      {/* Search Bar */}
      <div className="p-4 border-b border-slate-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
          <input
            type="text"
            placeholder="Kart adı veya banka ara..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45] transition-colors"
          />
        </div>
      </div>

      {/* Active Filters */}
      {Object.values(selectedFilters).some(arr => arr.length > 0) && (
        <div className="p-4 bg-slate-50 border-b border-slate-200">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-slate-600">
              <Filter size={16} />
              <span className="text-sm font-medium">Aktif Filtreler</span>
            </div>
            <button
              onClick={onClearFilters}
              className="text-sm text-red-500 hover:text-red-600 font-medium flex items-center gap-1"
            >
              <X size={16} />
              Filtreleri Temizle
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(selectedFilters).map(([category, values]) =>
              values.map((value) => (
                <span
                  key={`${category}-${value}`}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#F7BA45]/10 text-slate-700 text-sm"
                >
                  {value}
                  <button
                    onClick={() => onFilterChange(category, value)}
                    className="ml-1 hover:text-red-500"
                  >
                    <X size={14} />
                  </button>
                </span>
              ))
            )}
          </div>
        </div>
      )}

      {/* Filter Groups */}
      <div className="p-4 space-y-6">
        {/* Card Category Filter */}
        <div>
          <h3 className="flex items-center gap-2 text-slate-800 font-medium mb-3">
            <CreditCard size={18} className="text-[#F7BA45]" />
            Kart Kategorisi
          </h3>
          <div className="space-y-2">
            {filters.cardCategory.map((category) => (
              <label key={category} className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={selectedFilters.cardCategory.includes(category)}
                  onChange={() => onFilterChange('cardCategory', category)}
                  className="rounded border-slate-300 text-[#F7BA45] focus:ring-[#F7BA45]"
                />
                <span className="text-sm text-slate-600 group-hover:text-slate-900">{category}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Card Type Filter */}
        <div>
          <h3 className="flex items-center gap-2 text-slate-800 font-medium mb-3">
            <Tag size={18} className="text-[#F7BA45]" />
            Kart Tipi
          </h3>
          <div className="space-y-2">
            {filters.cardType.map((type) => (
              <label key={type} className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={selectedFilters.cardType.includes(type)}
                  onChange={() => onFilterChange('cardType', type)}
                  className="rounded border-slate-300 text-[#F7BA45] focus:ring-[#F7BA45]"
                />
                <span className="text-sm text-slate-600 group-hover:text-slate-900">{type}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Annual Fee Filter */}
        <div>
          <h3 className="flex items-center gap-2 text-slate-800 font-medium mb-3">
            <Wallet size={18} className="text-[#F7BA45]" />
            Yıllık Ücret
          </h3>
          <div className="space-y-2">
            {filters.annualFee.map((fee) => (
              <label key={fee} className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={selectedFilters.annualFee.includes(fee)}
                  onChange={() => onFilterChange('annualFee', fee)}
                  className="rounded border-slate-300 text-[#F7BA45] focus:ring-[#F7BA45]"
                />
                <span className="text-sm text-slate-600 group-hover:text-slate-900">{fee}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Bank Filter */}
        <div>
          <h3 className="flex items-center gap-2 text-slate-800 font-medium mb-3">
            <Building2 size={18} className="text-[#F7BA45]" />
            Banka
          </h3>
          <div className="space-y-2">
            {filters.bank.map((bank) => (
              <label key={bank} className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={selectedFilters.bank.includes(bank)}
                  onChange={() => onFilterChange('bank', bank)}
                  className="rounded border-slate-300 text-[#F7BA45] focus:ring-[#F7BA45]"
                />
                <span className="text-sm text-slate-600 group-hover:text-slate-900">{bank}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Features Filter */}
        <div>
          <h3 className="flex items-center gap-2 text-slate-800 font-medium mb-3">
            <Gift size={18} className="text-[#F7BA45]" />
            Özellikler
          </h3>
          <div className="space-y-2">
            {filters.features.map((feature) => (
              <label key={feature} className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={selectedFilters.features.includes(feature)}
                  onChange={() => onFilterChange('features', feature)}
                  className="rounded border-slate-300 text-[#F7BA45] focus:ring-[#F7BA45]"
                />
                <span className="text-sm text-slate-600 group-hover:text-slate-900">{feature}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardFilters;